const BASE_API_URL = import.meta.env.VITE_REACT_APP_BASE_API_URL;
// const BASE_API_URL = 'https://api.djkip.com'; //uncomment this to connect to prod BE

import { trigger } from './utils/events';
import { auth } from './firebase';
import tokenManager from './utils/TokenManager';
import { isAuthDebugEnabled } from './utils/authDebug';

export default class ApiClient {
  constructor() {
    this.base_url = BASE_API_URL + '/api';
    this.retryQueue = new Map(); // Track retry attempts per request
    this.backgroundMode = false;
    this.debugMode = isAuthDebugEnabled();
    this.requestIdCounter = 0;

    this.setupBackgroundDetection();

    if (this.debugMode) {
      console.log('🔧 ApiClient initialized');
    }
  }

  debug(message, data = null) {
    if (this.debugMode) {
      console.log(`🔧 ApiClient: ${message}`, data);
    }
  }

  setupBackgroundDetection() {
    // Detect when app goes to background (for mobile web)
    document.addEventListener('visibilitychange', () => {
      const wasBackground = this.backgroundMode;
      this.backgroundMode = document.hidden;

      if (wasBackground !== this.backgroundMode) {
        this.debug('Background mode changed', {
          backgroundMode: this.backgroundMode,
        });

        if (!this.backgroundMode) {
          // App came to foreground, process queued requests
          this.processQueuedRequests();
        }
      }
    });

    // Listen for audio context state changes
    if (window.AudioContext || window.webkitAudioContext) {
      try {
        this.audioContext = new (window.AudioContext ||
          window.webkitAudioContext)();
        this.audioContext.addEventListener('statechange', () => {
          const audioSuspended = this.audioContext.state === 'suspended';
          this.debug('Audio context state changed', {
            state: this.audioContext.state,
            audioSuspended,
          });
        });
      } catch (error) {
        this.debug('Audio context not available', error);
      }
    }
  }

  generateRequestId() {
    return `req_${++this.requestIdCounter}_${Date.now()}`;
  }

  async request(options) {
    const requestId = this.generateRequestId();
    this.debug('Request started', {
      requestId,
      url: options.url,
      method: options.method,
    });

    // Skip auth for user registration
    if (options.url === '/users' && options.method === 'POST') {
      return this.requestInternal(options);
    }

    try {
      return await this.requestWithRetry(options, requestId);
    } catch (error) {
      this.debug('Request failed', { requestId, error: error.message });
      return this.handleRequestError(error, options, requestId);
    }
  }

  async requestWithRetry(options, requestId, attempt = 0) {
    const maxRetries = this.backgroundMode ? 3 : 1; // More retries in background
    const backoffDelay = Math.pow(2, attempt) * 1000; // Exponential backoff

    this.debug('Request attempt', {
      requestId,
      attempt,
      maxRetries,
      backgroundMode: this.backgroundMode,
    });

    try {
      // Get valid token
      const token = await this.getValidToken();

      // Add this check - if token is undefined/null, don't send the request
      if (!token) {
        this.debug('No valid token available, returning auth error');
        return {
          ok: false,
          status: 401,
          body: {
            code: 401,
            message: 'Authentication required',
            description: 'No valid token available',
          },
        };
      }

      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      };

      const response = await this.requestInternal(options);

      if (response.ok || response.status !== 401) {
        this.debug('Request successful', {
          requestId,
          status: response.status,
        });
        return response;
      }

      // Handle 401 errors with smart retry logic
      if (attempt < maxRetries) {
        this.debug('401 error, retrying', { requestId, attempt, maxRetries });

        await this.delay(backoffDelay);
        await tokenManager.refreshToken({ force: true });

        // Update token in options
        const newToken = await this.getValidToken();

        // Same check here
        if (!newToken) {
          this.debug('No valid token after refresh, returning auth error');
          return {
            ok: false,
            status: 401,
            body: {
              code: 401,
              message: 'Authentication required',
              description: 'Token refresh failed',
            },
          };
        }

        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${newToken}`,
        };

        return this.requestWithRetry(options, requestId, attempt + 1);
      }

      this.debug('Max retries reached', { requestId, attempt });
      return response;
    } catch (error) {
      // If getValidToken throws an error, return auth error instead of retrying
      this.debug('getValidToken failed, returning auth error', error);
      return {
        ok: false,
        status: 401,
        body: {
          code: 401,
          message: 'Authentication required',
          description: error.message,
        },
      };
    }
  }

  async getValidToken() {
    this.debug('Getting valid token');

    try {
      return await tokenManager.getValidToken();
    } catch (error) {
      this.debug('Failed to get valid token', error);
      throw error;
    }
  }

  async requestInternal(options) {
    let query = new URLSearchParams(options.query || {}).toString();
    if (query !== '') {
      query = '?' + query;
    }

    let response;
    try {
      this.debug('Making internal request', {
        url: this.base_url + options.url + query,
        method: options.method,
      });

      response = await fetch(this.base_url + options.url + query, {
        method: options.method,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        body: options.body ? JSON.stringify(options.body) : null,
      });
    } catch (error) {
      this.debug('Network error', error);
      response = {
        ok: false,
        status: 500,
        json: async () => {
          return {
            code: 500,
            message: 'The server is unresponsive',
            description: error.toString(),
          };
        },
      };
    }

    return {
      ok: response.ok,
      status: response.status,
      body: response.status !== 204 ? await response.json() : null,
    };
  }

  async requestUnauthenticated(options) {
    let query = new URLSearchParams(options.query || {}).toString();
    if (query !== '') {
      query = '?' + query;
    }

    let response;
    try {
      response = await fetch(this.base_url + options.url + query, {
        method: options.method,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        body: options.body ? JSON.stringify(options.body) : null,
      });
    } catch (error) {
      response = {
        ok: false,
        status: 500,
        json: async () => {
          return {
            code: 500,
            message: 'The server is unresponsive',
            description: error.toString(),
          };
        },
      };
    }

    return {
      ok: response.ok,
      status: response.status,
      body: response.status !== 204 ? await response.json() : null,
    };
  }

  async get(url, query, options) {
    return this.request({ method: 'GET', url, query, ...options });
  }

  async post(url, body, options) {
    return this.request({ method: 'POST', url, body, ...options });
  }

  async put(url, body, options) {
    return this.request({ method: 'PUT', url, body, ...options });
  }

  async delete(url, options) {
    return this.request({ method: 'DELETE', url, ...options });
  }

  async logout() {
    this.debug('Logging out');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');
    tokenManager.cleanup();
    trigger('logout');
  }

  isAuthenticated() {
    return (
      auth.currentUser !== null || localStorage.getItem('accessToken') !== null
    );
  }

  // Utility methods
  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  processQueuedRequests() {
    this.debug('Processing queued requests');
    // Implementation for processing queued requests when app comes to foreground
  }

  handleRequestError(error, options, requestId) {
    this.debug('Handling request error', { requestId, error: error.message });

    return {
      ok: false,
      status: 500,
      body: {
        message: 'Request failed',
        error: error.message,
        requestId,
      },
    };
  }
}
